import type { FC, MouseEvent } from 'react';
import React, { useContext, createContext, useCallback } from 'react';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';

import {
    OFFSET_LIMIT_PAGINATION,
    DIRECTION_NEXT_TYPE,
    DIRECTION_PREV_TYPE,
    DIRECTION_NEXT_VALUE,
    DIRECTION_PREV_VALUE,
} from '../constants';
import type { TDirection } from '../models';
import { getPagesCount } from '../helpers';

import type { IPaginationContext, IPaginationProviderProps } from './models';
import { defaultPaginationContext } from './default-context';

export const PaginationContext = createContext<IPaginationContext>(defaultPaginationContext);
export const usePaginationContext = () => useContext(PaginationContext);

export const Pagination: FC<IPaginationProviderProps> = ({
    analytics,
    children,
    currentPageNumber = 1,
    hideArrows = false,
    limit = OFFSET_LIMIT_PAGINATION,
    pagesCount,
    elementsCount,
    onChangePage,
    onShowMoreButtonClick,
}) => {
    const pages = getPagesCount({ limit, pagesCount, elementsCount });

    const handlePageClick = useCallback(
        (pageNumber: number, direction?: TDirection) =>
            ({ event }: { event: MouseEvent<HTMLSpanElement | HTMLAnchorElement> }) => {
                if (direction === DIRECTION_NEXT_TYPE) {
                    sendEvent({
                        ...analytics?.paginationArrowsClick,
                        label_detail: DIRECTION_NEXT_VALUE,
                    });
                } else if (direction === DIRECTION_PREV_TYPE) {
                    sendEvent({
                        ...analytics?.paginationArrowsClick,
                        label_detail: DIRECTION_PREV_VALUE,
                    });
                } else {
                    sendEvent({
                        ...analytics?.paginationPageClick,
                        label_detail: `На страницу - ${pageNumber}`,
                    });
                }
                event.preventDefault();

                onChangePage(pageNumber);
            },
        [analytics?.paginationArrowsClick, analytics?.paginationPageClick, onChangePage]
    );

    const handleShowMoreButtonClick = () => {
        sendEvent(analytics?.paginationShowMoreClick);
        onShowMoreButtonClick();
        // Отключение сохранения позиции скролла
        // https://jira.moscow.alfaintra.net/browse/SITEMLDIVE-1772
        if (history && history.scrollRestoration) {
            history.scrollRestoration = 'manual';
        }
    };

    if (pages === 1) {
        return null;
    }

    return (
        <section data-test-id="pagination">
            <PaginationContext.Provider
                value={{
                    handlePageClick,
                    handleShowMoreButtonClick,
                    currentPageNumber,
                    hideArrows,
                    pages,
                }}
            >
                {children}
            </PaginationContext.Provider>
        </section>
    );
};
